import React from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useParams } from "react-router-dom";
import moment from "moment";

import { Blockquote, Module, ModuleBody } from "@wfp/ui";

import ModalExtended from "components/ModalExtended";
import ButtonRouter from "components/ButtonRouter";
import CommoditySelect from "components/CommoditySelect";
import CurrencyInput from "components/CurrencyInput";
import ErrorList from "components/ErrorList";
import MeasureUnitValue from "components/MeasureUnitValue";
import QuantityInput from "components/QuantityInput";
import SubTitle from "components/SubTitle";
import TableExtended from "components/TableExtended";
import AddButton from "components/RepeaterAddButton";
import VendorEdit from "components/CashToSchool/vendorEdit";
import { Repeater, RepeaterItem, RepeaterTitle } from "components/Repeater";

import useStore from "helpers/useStore";
import commoditiesDuck from "data-handler/ducks/commodities";
import {
  getAllSchoolYears,
  getAllStores,
  getIsSyncableLocked,
  isDeliveryStore,
  isPurchaseDetailStore,
  resetStore,
  SchoolYear,
  Store,
} from "data-handler/ducks/stores";
import { deliveryCategory, purchaseDetailCategory } from "SCConstants";
import { RootState } from "data-handler/rootReducer";

import "./styles.scss";

type InitialStockEditProps = {
  currentStoreData?: SchoolYear;
};

const createInitialStock = (values: any, dateTime: any) => {
  const stock_events = (values?.commodities || [])
    .filter((item: any) => item.quantity >= 0)
    .reduce((acc: any, cur: any) => {
      const list = [...acc];
      if (parseFloat(cur.quantity) > 0) {
        list.push({
          commodity: cur.commodity,
          quantity: cur.quantity,
          category: deliveryCategory,
          sign: 1,
          type: "DEL",
          batch_no: "unknown",
          issues: [],
          meta: {
            is_carry_over: true,
            batch_no: "unknown",
            comments: "",
          },
        });
      }
      return list;
    }, []);

  return {
    event_type: "delivery",
    stock_events,
    occurred_at: moment(dateTime, "YYYY-MM-DD HH:mm").toISOString(),
    meta: {
      waybill_no: "Initial stock",
      is_initial_stock: true,
      is_negative_stock_rebalance: false,
    },
    category: deliveryCategory,
  };
};

const createInitialPurchaseStock = (values: any, dateTime: any) => {
  const stock_events = (values?.purchaseCommodities || [])
    .filter((item: any) => item.quantity >= 0)
    .reduce((acc: any, cur: any) => {
      const list = [...acc];
      if (parseFloat(cur.quantity) > 0) {
        list.push({
          commodity: cur.commodity,
          quantity: cur.quantity,
          category: purchaseDetailCategory,
          sign: 1,
          type: "PUR",
          total_paid: cur.total_paid || "0",
          issues: [],
          meta: {
            is_carry_over: true,
            comments: "",
          },
        });
      }
      return list;
    }, []);

  const totalPaid = stock_events.reduce((sum: number, se: any) => {
    return sum + (parseFloat(se.total_paid) || 0);
  }, 0);

  return {
    event_type: "purchase_detail",
    stock_events,
    occurred_at: moment(dateTime, "YYYY-MM-DD HH:mm").toISOString(),
    vendor: values.vendor || null,
    other_costs: [],
    meta: {
      receipt_no: "Initial purchase stock",
      is_initial_stock: true,
      is_vendor_created: false,
      total_paid: totalPaid,
      vendor_category_id: values.vendor_category || 1,
    },
    category: purchaseDetailCategory,
  };
};

/**
 * Modal which manages the initial stock of the school's FIRST school year
 * (later school years carry stock over instead, see CarryOverEdit).
 *
 * While the school year has not been synced yet, commodities and quantities
 * can be freely edited; saving (re)builds the "Initial stock" delivery and
 * the "Initial purchase stock" purchase. Once the school year has been
 * synced, the modal becomes read-only and links to those records on the
 * Deliveries page.
 */
const InitialStockEdit: React.ElementType<InitialStockEditProps> = ({
  currentStoreData,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const params: Record<string, string> = useParams();
  const {
    updateStore,
    closeModal,
    submitBtnRef,
    submitViaBtnRefClick,
  } = useStore();

  const yearPath = `/school/${params.schoolId}/info/${params.item}`;

  const allStores = useSelector(getAllStores);
  const allSchoolYears = useSelector(getAllSchoolYears);
  const commodityIndex = useSelector(commoditiesDuck.getIndex);

  const isLocked = useSelector((state: RootState) =>
    getIsSyncableLocked(state, currentStoreData)
  );

  const startsOn = moment(currentStoreData?.starts_on).format("YYYY-MM-DD");

  const findInitialStockStore = (isStoreOfKind: (store: Store) => boolean) =>
    allStores.find((store: any) => {
      const date = store?.occurred_at?.split("T")[0];
      return (
        isStoreOfKind(store) &&
        date === startsOn &&
        store.meta?.is_initial_stock === true &&
        store.type !== "delete"
      );
    });

  const initialStockDelivery: any = findInitialStockStore(isDeliveryStore);
  const initialStockPurchase: any = findInitialStockStore(
    isPurchaseDetailStore
  );

  const isSynced = Boolean(currentStoreData?.id);
  const isReadOnly = isSynced || isLocked;

  const eventToRow = (event: any) => ({
    commodity: event.commodity,
    quantity: parseFloat(event.quantity),
    measure_unit:
      event.measure_unit || commodityIndex[event.commodity]?.measure_unit,
  });

  const commoditiesList = (initialStockDelivery?.stock_events || []).map(
    eventToRow
  );
  const purchaseCommoditiesList = (
    initialStockPurchase?.stock_events || []
  ).map((event: any) => ({
    ...eventToRow(event),
    total_paid: event.total_paid || "0",
  }));

  const methods = useForm({
    defaultValues: {
      commodities: commoditiesList,
      purchaseCommodities: purchaseCommoditiesList,
    },
  });

  const {
    control,
    handleSubmit,
    errors,
    register,
    watch,
    getValues,
    setValue,
  } = methods;

  const {
    fields: commodityRows,
    append: appendCommodity,
    remove: removeCommodity,
  } = useFieldArray({
    control,
    name: "commodities",
  });
  const {
    fields: purchaseCommodityRows,
    append: appendPurchaseCommodity,
    remove: removePurchaseCommodity,
  } = useFieldArray({
    control,
    name: "purchaseCommodities",
  });

  const onSubmit = (formValues: any) => {
    const values = {
      ...formValues,
      commodities: (formValues.commodities || []).filter(
        (c: any) => c.commodity
      ),
      purchaseCommodities: (formValues.purchaseCommodities || []).filter(
        (c: any) => c.commodity
      ),
    };

    const date = moment(currentStoreData!.starts_on).format("YYYY-MM-DD");
    const dateTime = moment(date + " 13:00");

    let dispatched = false;

    const dispatchInitialStockStore = (
      storeValues: any,
      existingStore: any,
      category: string
    ) => {
      if (storeValues.stock_events.length > 0) {
        updateStore(
          {
            id: existingStore?.client_id,
            values: storeValues,
            type: existingStore ? "update" : "create",
            category,
          },
          yearPath
        );
        dispatched = true;
      } else if (
        existingStore &&
        existingStore.sync === false &&
        !existingStore.id
      ) {
        // All rows were removed: drop the never-synced store again
        dispatch(resetStore(existingStore.client_id));
      }
    };

    dispatchInitialStockStore(
      createInitialStock(values, dateTime),
      initialStockDelivery,
      deliveryCategory
    );
    dispatchInitialStockStore(
      createInitialPurchaseStock(values, dateTime),
      initialStockPurchase,
      purchaseDetailCategory
    );

    if (!dispatched) {
      closeModal(undefined, yearPath);
    }
  };

  // Initial stock only exists for the school's very first school year
  const isFirstSchoolYear =
    currentStoreData !== undefined &&
    !allSchoolYears.some(
      (year: SchoolYear) =>
        year.client_id !== currentStoreData.client_id &&
        year.type !== "delete" &&
        moment(year.starts_on).isBefore(currentStoreData.starts_on, "day")
    );

  if (!currentStoreData || currentStoreData.model !== "year") {
    return <Redirect to={`/school/${params.schoolId}/info`} />;
  }
  if (!isFirstSchoolYear) {
    return <Redirect to={yearPath} />;
  }

  const commodityName = (commodityId: any) =>
    commodityIndex[commodityId]?.name || `Unidentified(${commodityId})`;

  const hasInitialStock =
    (initialStockDelivery?.stock_events || []).length > 0 ||
    (initialStockPurchase?.stock_events || []).length > 0;

  const renderReadOnlyTable = (
    titleMessage: React.ReactNode,
    sourceStore: any,
    showTotalPaid: boolean,
    sourceLabel: React.ReactNode
  ) =>
    (sourceStore?.stock_events || []).length > 0 && (
      <div>
        <SubTitle>{titleMessage}</SubTitle>
        <Module noMargin>
          <ModuleBody noPadding>
            <TableExtended noBorder>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage
                      id="CommoditySelect.commodityInputLabel"
                      defaultMessage="Commodity"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="DeliveriesEdit.quantity"
                      defaultMessage="Quantity"
                    />
                  </th>
                  {showTotalPaid && (
                    <th>
                      <FormattedMessage
                        id="PurchaseEdit.totalPaid"
                        defaultMessage="Total paid"
                      />
                    </th>
                  )}
                  <th />
                </tr>
              </thead>
              <tbody>
                {sourceStore.stock_events.map((event: any, index: number) => (
                  <tr key={index}>
                    <td>{commodityName(event.commodity)}</td>
                    <td>
                      <MeasureUnitValue
                        measureUnit={
                          event.measure_unit ||
                          commodityIndex[event.commodity]?.measure_unit
                        }
                      >
                        {parseFloat(event.quantity)}
                      </MeasureUnitValue>
                    </td>
                    {showTotalPaid && <td>{parseFloat(event.total_paid)}</td>}
                    <td className="initial-stock-edit__sources">
                      {index === 0 && (
                        <ButtonRouter
                          kind="ghost"
                          small
                          to={`/school/${params.schoolId}/deliveries/${sourceStore.client_id}`}
                        >
                          {sourceLabel}
                        </ButtonRouter>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableExtended>
          </ModuleBody>
        </Module>
      </div>
    );

  return (
    <ModalExtended
      hideClose={!isReadOnly}
      passiveModal={isReadOnly}
      onRequestSubmit={isReadOnly ? undefined : submitViaBtnRefClick}
      onRequestClose={
        isReadOnly ? () => closeModal(undefined, yearPath) : () => {}
      }
      onSecondarySubmit={() => closeModal(undefined, yearPath)}
      modalLabel={intl.formatMessage({
        id: "InitialStockEdit.modalLabel",
        defaultMessage: "School year",
      })}
      modalHeading={intl.formatMessage({
        id: "InitialStockEdit.modalHeading",
        defaultMessage: "Initial stock",
      })}
      primaryButtonText={intl.formatMessage({
        id: "InitialStockEdit.save",
        defaultMessage: "Save",
      })}
      className="initial-stock-edit"
    >
      {isReadOnly ? (
        <div className="wfp--form wfp--form-long">
          <Blockquote kind="info">
            <p>
              <FormattedMessage
                id="InitialStockEdit.readOnlyNotice"
                defaultMessage="The initial stock has been saved and synced. To make further adjustments, open the corresponding delivery or purchase from the Deliveries page, using the links below."
              />
            </p>
          </Blockquote>
          {!hasInitialStock && (
            <Blockquote kind="warning">
              <p>
                <FormattedMessage
                  id="InitialStockEdit.noInitialStock"
                  defaultMessage="No initial stock was recorded for this school year."
                />
              </p>
            </Blockquote>
          )}
          {renderReadOnlyTable(
            <FormattedMessage
              id="YearEdit.openingStock"
              defaultMessage="Opening stock for on-site school meals (WFP)"
            />,
            initialStockDelivery,
            false,
            <FormattedMessage
              id="InitialStockEdit.viewDelivery"
              defaultMessage="View delivery"
            />
          )}
          {renderReadOnlyTable(
            <FormattedMessage
              id="YearEdit.openingPurchaseStock"
              defaultMessage="Opening stock for purchases"
            />,
            initialStockPurchase,
            true,
            <FormattedMessage
              id="InitialStockEdit.viewPurchase"
              defaultMessage="View purchase"
            />
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="wfp--form wfp--form-long">
            <ErrorList errors={errors} />
            <Blockquote kind="info">
              <p>
                <FormattedMessage
                  id="YearEdit.openingStockInfo"
                  defaultMessage="Please insert the food stock levels on the school year start date, which you entered above. This is needed to ensure, that the stock levels in the system always correspond to your real stock levels."
                />
              </p>
            </Blockquote>
            <Repeater>
              <RepeaterTitle>
                <FormattedMessage
                  id="YearEdit.openingStock"
                  defaultMessage="Opening stock for on-site school meals (WFP)"
                />
              </RepeaterTitle>
              {commodityRows.map((commodityRow: any, index) => (
                <RepeaterItem
                  key={commodityRow.id}
                  removeAction={() => removeCommodity(index)}
                  className="delivery-edit__commodities"
                >
                  <Controller
                    render={({ onChange }) => (
                      <CommoditySelect
                        className="wfp--react-select-container"
                        wfpOnly
                        category={deliveryCategory}
                        required
                        key={`commodities[${index}].commodity`}
                        onChange={(e: any) => {
                          setValue(
                            `commodities[${index}].measure_unit`,
                            e.measure_unit
                          );
                          onChange(e.value);
                        }}
                        value={commodityRow?.commodity}
                        control={control}
                        invalidOptions={commodityRows}
                      />
                    )}
                    name={`commodities[${index}].commodity`}
                    control={control}
                    rules={{
                      required: intl.formatMessage({
                        id: "Form.required",
                        defaultMessage: "Required",
                      }),
                    }}
                  />
                  <Controller
                    render={({ onChange, onBlur, value }) => (
                      <QuantityInput
                        control={control}
                        value={value}
                        onChange={(e: any) => {
                          watch(`commodities[${index}].quantity`);
                          onChange(
                            parseFloat(
                              (parseFloat(e.target.value) || 0).toFixed(3)
                            )
                          );
                        }}
                        onBlur={onBlur}
                        labelText={
                          <FormattedMessage
                            id="DeliveriesEdit.quantity"
                            defaultMessage="Quantity"
                          />
                        }
                        setValue={setValue}
                        name={`commodities[${index}].quantity`}
                        invalid={errors?.commodities?.[index]?.quantity}
                        invalidText={
                          errors?.commodities?.[index]?.quantity?.message
                        }
                        measureUnit={watch(
                          `commodities[${index}].measure_unit`
                        )}
                        allowZero={true}
                      />
                    )}
                    control={control}
                    name={`commodities[${index}].quantity`}
                  />
                  <Controller
                    render={() => <input type="hidden" />}
                    name={`commodities[${index}].measure_unit`}
                    control={control}
                  />
                </RepeaterItem>
              ))}
            </Repeater>
            <AddButton
              onClick={() => {
                appendCommodity({});
              }}
            />

            <Repeater>
              <RepeaterTitle>
                <FormattedMessage
                  id="YearEdit.openingPurchaseStock"
                  defaultMessage="Opening stock for purchases"
                />
              </RepeaterTitle>
              {purchaseCommodityRows.map((commodityRow: any, index) => (
                <RepeaterItem
                  key={commodityRow.id}
                  removeAction={() => removePurchaseCommodity(index)}
                  className="delivery-edit__commodities"
                >
                  <Controller
                    render={({ onChange }) => (
                      <CommoditySelect
                        className="wfp--react-select-container"
                        category={purchaseDetailCategory}
                        required
                        key={`purchaseCommodities[${index}].commodity`}
                        onChange={(e: any) => {
                          setValue(
                            `purchaseCommodities[${index}].measure_unit`,
                            e.measure_unit
                          );
                          onChange(e.value);
                        }}
                        value={commodityRow?.commodity}
                        control={control}
                        invalidOptions={purchaseCommodityRows}
                      />
                    )}
                    name={`purchaseCommodities[${index}].commodity`}
                    control={control}
                    rules={{
                      required: intl.formatMessage({
                        id: "Form.required",
                        defaultMessage: "Required",
                      }),
                    }}
                  />
                  <div className="quantity-container">
                    <Controller
                      render={({ onChange, onBlur, value }) => (
                        <QuantityInput
                          control={control}
                          value={value}
                          onChange={(e: any) => {
                            watch(`purchaseCommodities[${index}].quantity`);
                            onChange(
                              parseFloat(
                                (parseFloat(e.target.value) || 0).toFixed(3)
                              )
                            );
                          }}
                          onBlur={onBlur}
                          labelText={
                            <FormattedMessage
                              id="DeliveriesEdit.quantity"
                              defaultMessage="Quantity"
                            />
                          }
                          setValue={setValue}
                          name={`purchaseCommodities[${index}].quantity`}
                          invalid={
                            errors?.purchaseCommodities?.[index]?.quantity
                          }
                          invalidText={
                            errors?.purchaseCommodities?.[index]?.quantity
                              ?.message
                          }
                          measureUnit={watch(
                            `purchaseCommodities[${index}].measure_unit`
                          )}
                          allowZero={true}
                        />
                      )}
                      control={control}
                      name={`purchaseCommodities[${index}].quantity`}
                    />
                    <Controller
                      render={({ onChange, value }) => (
                        <CurrencyInput
                          value={value}
                          onChange={(onChangeValue: any) => {
                            onChange(onChangeValue ? onChangeValue : 0);
                          }}
                          labelText={
                            <FormattedMessage
                              id="PurchaseEdit.totalPaid"
                              defaultMessage="Total paid"
                            />
                          }
                          setValue={setValue}
                          control={control}
                          name={`purchaseCommodities[${index}].total_paid`}
                          id={`purchaseCommodities[${index}].total_paid`}
                          invalid={
                            !!errors?.purchaseCommodities?.[index]?.total_paid
                          }
                          invalidText={
                            errors?.purchaseCommodities?.[index]?.total_paid
                              ?.message
                          }
                        />
                      )}
                      control={control}
                      name={`purchaseCommodities[${index}].total_paid`}
                    />
                  </div>
                  <Controller
                    render={() => <input type="hidden" />}
                    name={`purchaseCommodities[${index}].measure_unit`}
                    control={control}
                  />
                </RepeaterItem>
              ))}
            </Repeater>
            <AddButton
              onClick={() => {
                appendPurchaseCommodity({});
              }}
            />

            {purchaseCommodityRows.length > 0 && (
              <FormProvider {...methods}>
                <VendorEdit
                  control={control}
                  watch={watch}
                  errors={errors}
                  setValue={setValue}
                  getValues={getValues}
                  register={register}
                  purchaseCurrentStoreData={initialStockPurchase}
                />
              </FormProvider>
            )}
          </div>
          <button className="hidden-btn" ref={submitBtnRef as any} />
        </form>
      )}
    </ModalExtended>
  );
};

export default InitialStockEdit;
